import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useModalControl } from "../../customeHooks/useModalControl";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { AuthProfile } from "../../redux/features/auth/auth.types";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { persistor } from "../../redux/provider";
import { adminProfileCookieKey } from "../../utils/constants";
import { getCookie, regexText } from "../../utils/helpers";
import { Button } from "../buttons/button";
import { ProfileIcon } from "../cards/profilePic";
import Dropdown from "../dropdown/dropdown";
import { Icon } from "../icon/icon";
import { MobileNavModal } from "../modal/app_modals/mobileNavModal";
import { ScrollToTop } from "../scrollToTop";

const testArray = [
  /^\/events\/[^/]+\/published$/,
  /^\/events\/.+\/add-payout\/.+$/,
  /^\/complete-profile$/,
  /^\/events\/[^/]+\/$/,
  /^\/events\/[^/]+\/preview$/,
];

const mobileTestArray = [...testArray];

const navs = [
  {
    title: "Overview",
    to: "/overview",
    icon: "home-icon",
    height: 21,
    width: 21,
  },
  {
    title: "Users",
    to: "/users",
    icon: "users",
  },
  {
    title: "Events",
    to: "/events",
    icon: "calendar",
  },

  // {
  //   title: "Revenue",
  //   to: "/revenue",
  //   icon: "hand-coins",
  // },

  {
    title: "Sales",
    to: "/sales",
    icon: "megaphone",
  },
];

export const AdminAppLayout = ({ children }: { children: ReactNode }) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const pageRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  const { scrollYProgress } = useScroll({ container: pageRef });

  useMotionValueEvent(scrollYProgress, "change", (val) => {
    setScrollProgress(val);
  });

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { handleModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl();

  const { isAdminSignedIn } = useAppSelector((state) => state.auth);

  const dropDownOptions = [
    {
      icon: "settings",
      label: "Settings",
      onClick: () => {
        navigate("/profile");
      },
    },
    {
      icon: "logout",
      label: "Logout",
      onClick: () => {
        dispatch(authSliceActions.signAdminOut());
        persistor.purge();
      },
    },
  ];

  useEffect(() => {
    if (pageRef.current) {
      pageRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });

      setScrollProgress(0);
    }
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop
        changeKey={location.pathname}
        rerunOnKeyChange
        scrollIntoView
      />

      <div className="flex h-screen max-h-[100dvh] flex-col bg-light-grey-bg">
        {/* Top nav  */}
        <div
          className={classNames(
            "border-b border-grey-100 py-6 md:border-b-0 md:py-6",
            scrollProgress > 0.1
              ? "md:!border-b md:shadow-sm md:shadow-grey-900/20"
              : "",
            "!z-[998] transition-all duration-300",
            "bg-light-grey-bg",
          )}
        >
          <div className="max-width">
            <div className="relative flex items-center justify-between gap-4">
              <Link
                to={"/overview"}
                className="max-w-[120px] md:absolute md:left-0 md:max-w-max"
              >
                <img
                  src="/assets/logo_admin.svg"
                  className="w-full"
                  alt="logo"
                />
              </Link>

              {isAdminSignedIn &&
              !regexText(location.pathname, testArray) &&
              ![""].includes(location.pathname) ? (
                <div className="hidden flex-1 items-center justify-center gap-4 md:flex">
                  {navs.map((nav, idx) => {
                    const key = `${idx}-${nav.to}`;

                    return (
                      <NavLink
                        key={key}
                        to={nav.to ?? ""}
                        className={({ isActive }) => {
                          return `soft-shrink flex items-center gap-2.5 rounded-2xl px-5 py-3 font-bold ${isActive && "bg-white"} `;
                        }}
                      >
                        <Icon
                          svg={nav.icon}
                          height={nav.height ?? 20}
                          width={nav.width ?? 20}
                          className=""
                        />
                        {nav.title}
                      </NavLink>
                    );
                  })}
                </div>
              ) : (
                <span className="opacity-0">p</span>
              )}

              {isAdminSignedIn ? (
                <>
                  <ProfileDropdown
                    className={"hidden md:absolute md:right-0 md:inline-block"}
                    useCookieProfile={false}
                    dropDownOptions={dropDownOptions}
                  />

                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-full bg-white px-3 py-2 text-sm font-bold md:absolute md:right-0 md:hidden"
                    onClick={() => {
                      handleModalOpen("mobileNavModal");
                    }}
                  >
                    <Icon svg="caret-down" width={24} height={24} />
                    <span>Menu</span>
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-3 md:absolute md:right-0">
                  <Button
                    type="button"
                    text="Log in"
                    onClick={() => {
                      navigate("/login");
                    }}
                    className="min-w-max max-w-fit"
                    variation="plain-grey"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* main screen  */}
        <div id="pageTopRefToScroll" className="h-0" />
        <div
          className="max-width flex flex-1 flex-col overflow-y-scroll"
          ref={pageRef}
          id="appMainBody"
        >
          <div className="h-max w-full py-8">{children}</div>
        </div>

        {/* bottom mobile nav  */}
        <div
          className={classNames(`max-width relative py-4 md:hidden`, {
            "!hidden": regexText(location.pathname, mobileTestArray),
          })}
        >
          <div className="grid auto-cols-[minmax(100px,_1fr)] grid-flow-col items-center overflow-x-scroll">
            {navs.map((nav, idx) => {
              const key = `${idx}-${nav.to}`;

              return (
                <NavLink
                  key={key}
                  to={nav.to ?? ""}
                  className={({ isActive }) => {
                    return `soft-shrink flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold ${isActive && "bg-white"} `;
                  }}
                >
                  <Icon svg={nav.icon} height={20} width={20} className="" />
                  {nav.title}
                </NavLink>
              );
            })}
          </div>
        </div>

        <MobileNavModal
          open={modalOpen && modal === "mobileNavModal"}
          handleClose={handleModalClose}
          options={dropDownOptions}
          isAdmin
        />
      </div>
    </>
  );
};

export const AdminProfileDetails = ({
  className = "",
  profileClassName = "!p-3",
  profileDimension = 64,
  useCookieProfile = false,
}: {
  className?: string;
  profileClassName?: string;
  profileDimension?: number;
  useCookieProfile?: boolean;
}) => {
  const { adminProfile: stateProfile } = useAppSelector((state) => state.auth);

  const cookieProfile: AuthProfile = getCookie(adminProfileCookieKey)
    ? JSON.parse(getCookie(adminProfileCookieKey)!)
    : undefined;

  const profile = useCookieProfile ? cookieProfile : stateProfile;

  return (
    <div
      className={`flex flex-col items-center justify-center rounded-md bg-white p-3 text-center ${className}`}
    >
      <ProfileIcon
        dimension={profileDimension}
        className={profileClassName}
        img={undefined}
      />

      <p className="mt-2 text-base font-bold">
        {profile?.firstName} {profile?.lastName}
      </p>
      <p className="text-sm text-grey-500">{profile?.email}</p>
    </div>
  );
};

export const ProfileDropdown = ({
  dropDownOptions,
  useCookieProfile = false,
  className = "",
}: {
  dropDownOptions: {
    icon: string;
    label: string;
    onClick: () => void;
  }[];
  useCookieProfile?: boolean;
  className?: string;
}) => {
  const { adminProfile: stateProfile } = useAppSelector((state) => state.auth);

  const cookieProfile: AuthProfile = getCookie(adminProfileCookieKey)
    ? JSON.parse(getCookie(adminProfileCookieKey)!)
    : undefined;

  const profile = useCookieProfile ? cookieProfile : stateProfile;

  return (
    <Dropdown
      menuItems={[
        {
          name: (
            <AdminProfileDetails
              className="!cursor-default !items-start !justify-start !p-3"
              profileDimension={32}
              profileClassName="!p-[6px]"
              useCookieProfile={useCookieProfile}
            />
          ),
          onClick: (e) => {
            e?.preventDefault();
            e?.stopPropagation();
          },
        },
        ...dropDownOptions.map((option) => {
          return {
            name: (
              <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                <Icon
                  svg={option.icon}
                  width={20}
                  height={20}
                  className={`!min-w-fit ${option.icon === "logout" ? "text-danger" : "text-grey-400"}`}
                />
                <span
                  className={`${option.icon === "logout" && "text-danger"}`}
                >
                  {option.label}
                </span>
              </p>
            ),
            onClick: option.onClick,
          };
        }),
      ]}
      className={className}
      menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !min-w-[300px] !z-[998]"
      menuItemsClassName="border-none !text-base !bg-transparent !p-0"
      position="bottom end"
    >
      <button className="flex w-fit items-center gap-1">
        <p className="hidden max-w-[154px] items-center gap-[6px] rounded-full bg-white py-2.5 pl-2 pr-3 text-base font-bold lg:flex">
          <ProfileIcon img="" dimension={32} className="" />

          <span className="line-clamp-1 block text-ellipsis">
            {profile?.firstName}
          </span>
        </p>

        <Icon
          svg="caret-down"
          width={52}
          height={52}
          className="rounded-full bg-white p-[14px] text-black"
        />
      </button>
    </Dropdown>
  );
};
